/**
 * Console entry point.
 *
 * Boot order matters: in mock mode the MSW worker is started *before* React renders, so
 * the first query has a handler in place. If the worker cannot start, the console says so
 * loudly instead of silently falling through to the network — a demo that quietly hits
 * the real API would be worse than a visible failure.
 */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import './styles.css'
import { router } from './router'
import { isMockEnabled } from './mocks/mode'

const MOCK_BOOT_TIMEOUT_MS = 15_000

function Fatal({ message }: { message: string }) {
  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <h1 className="text-[20px] font-semibold text-mist-100">Console could not start</h1>
      <p className="mt-2 text-[13.5px] text-mist-300">{message}</p>
      <p className="num mt-4 text-[12px] text-mist-500">
        mode: {String(import.meta.env.VITE_API_MODE ?? 'mock (default)')} · build{' '}
        {import.meta.env.MODE}
      </p>
    </div>
  )
}

async function bootstrap(): Promise<void> {
  const root = document.getElementById('root')
  if (!root) throw new Error('#root missing from index.html')

  let mockFailure: string | null = null
  if (isMockEnabled()) {
    const { startMockWorker } = await import('./mocks/browser')
    const started = await Promise.race([
      startMockWorker(),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), MOCK_BOOT_TIMEOUT_MS)),
    ])
    if (!started) {
      mockFailure =
        'This build serves its API from MSW mocks, but the mock service worker did not start. Nothing would answer /api/v1, so the console refuses to pretend.'
    }
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: 0 },
    },
  })

  createRoot(root).render(
    <StrictMode>
      {mockFailure ? (
        <Fatal message={mockFailure} />
      ) : (
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      )}
    </StrictMode>,
  )
}

void bootstrap()
